import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import { getToken } from "../lib/tokenStorage";
import { EmailLink, PhoneLink, formatDuration, fmtDate } from "../lib/formatters";
import { toast } from "sonner";
import { FileText, Trash2, Plus, X, Save, Pencil, Info } from "lucide-react";
import { useAuth } from "../lib/auth";
import WeekendRevenueChart from "../components/WeekendRevenueChart";
import PendingPartnerPaymentsCard from "../components/PendingPartnerPaymentsCard";
import ConfirmDialog from "../components/ConfirmDialog";
import SalesToolbar from "../components/sales/SalesToolbar";
import BulkActionBar from "../components/BulkActionBar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const CURR = ["USD","EUR","GBP","INR"];
const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };
const GST_OPTIONS = [
  { v: -1, label: "Inclusive" },
  { v: 0, label: "None" },
  { v: 5, label: "5%" },
  { v: 10, label: "10%" },
  { v: 15, label: "15%" },
  { v: 18, label: "18%" },
  { v: 20, label: "20%" },
];

function currentFY() {
  const d = new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(2)}`;
}
function fyOptions() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const out = [];
  for (let i = 0; i < 5; i++) out.push(`${y - i}-${String(y - i + 1).slice(2)}`);
  return out;
}

export default function Sales() {
  const { user } = useAuth();
  const isFounder = user.role === "FOUNDER";
  const [rows, setRows] = useState([]);
  const [partners, setPartners] = useState([]);
  const [edits, setEdits] = useState({});
  const [tab, setTab] = useState("active"); // active | past
  const [fy, setFy] = useState(currentFY());
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const [standalone, setStandalone] = useState({ customer:"", programme:"Package Tour Program", duration:"", start_date:"", end_date:"", email:"", telephone:"", currency:"USD", amount:"", partner_name:"", partner_id:"", gst_percent:0 });
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { saved, total_by_currency, top_partners }
  const [confirmingInvoice, setConfirmingInvoice] = useState(null); // {row}
  const [backfillPreview, setBackfillPreview] = useState(null); // {previews, ...}
  // Channel filter tabs — "all" (default) shows every kind, "program"/"weekend"/
  // "standalone" filter by row.kind, and "partner" groups rows under a bold
  // Partner header row so Founder can eyeball each account's contribution.
  const [channel, setChannel] = useState(() => localStorage.getItem("aii-sales-channel") || "all");
  // Partner-name filter — narrows the By Partner (and every other) view to a
  // single account so Founder can zoom into one partner's revenue without
  // scanning the entire list.  Empty string = every partner.
  const [partnerFilter, setPartnerFilter] = useState("");

  // Rows that the user has explicitly re-opened for editing after they were
  // already saved. Saved-but-unlocked rows show the input controls again.
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  const isSaved = (row) => Boolean(row.partner_name && (row.amount || 0) > 0);
  const isLocked = (row) => isSaved(row) && !unlockedIds.has(row.id);
  const unlock = (id) => setUnlockedIds(s => { const n = new Set(s); n.add(id); return n; });
  const relock = (id) => setUnlockedIds(s => { const n = new Set(s); n.delete(id); return n; });

  const load = async () => {
    const [r, p, s] = await Promise.all([
      api.get(`/sales?archived=${tab === "past"}`),
      api.get("/partners"),
      api.get(`/sales/summary?fy=${fy}`).catch(() => ({ data: null })),
    ]);
    // Backend returns rows sorted ascending by start_date already.
    setRows(r.data);
    setPartners(p.data);
    setSummary(s.data);
    const eMap = {};
    for (const row of r.data) {
      // Reconcile partner_id: if row has a saved partner_id keep it; otherwise
      // best-effort match by name so the dropdown starts pre-selected.
      let pid = row.partner_id || "";
      if (!pid && row.partner_name) {
        const match = p.data.find(x => (x.company_name || x.name || "").toLowerCase() === row.partner_name.toLowerCase());
        if (match) pid = match.id;
      }
      eMap[row.id] = {
        currency: row.currency || "USD",
        amount: row.amount || "",
        partner_name: row.partner_name || "",
        partner_id: pid,
        gst_percent: row.gst_percent ?? 0,
      };
    }
    setEdits(eMap);
  };
  useEffect(() => { load(); }, [tab, fy]);

  const setEdit = (id, k, v) => setEdits(p => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const pickPartner = (id, partnerId) => {
    const p = partners.find(x => x.id === partnerId);
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], partner_id: partnerId, partner_name: p ? (p.company_name || p.name || "") : "" } }));
  };

  const save = async (row) => {
    const e = edits[row.id];
    if (!e.amount || !e.partner_id) { toast.error("Enter amount & pick a partner"); return; }
    await api.post("/sales", { booking_id: row.id, currency: e.currency, amount: parseFloat(e.amount), partner_name: e.partner_name, partner_id: e.partner_id, gst_percent: e.gst_percent === -1 ? 0 : parseFloat(e.gst_percent||0), gst_mode: e.gst_percent === -1 ? "inclusive" : "exclusive" });
    toast.success("Saved"); relock(row.id); await load();
  };

  const genInvoice = async (row) => {
    if (row.kind !== "standalone") await save(row);
    const token = getToken();
    window.open(`${API}/invoice/${row.id}?token=${token}`, "_blank");
  };

  const toggleSel = (id) => setSelected(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const genBatch = async () => {
    if (selected.size < 2) { toast.error("Select at least 2 bookings"); return; }
    // Save any pending amount edits for the selected rows first
    for (const id of selected) {
      const e = edits[id];
      if (e && e.amount && e.partner_id) {
        await api.post("/sales", { booking_id: id, currency: e.currency, amount: parseFloat(e.amount), partner_name: e.partner_name, partner_id: e.partner_id, gst_percent: e.gst_percent === -1 ? 0 : parseFloat(e.gst_percent||0), gst_mode: e.gst_percent === -1 ? "inclusive" : "exclusive" });
      }
    }
    const token = getToken();
    const ids = Array.from(selected).join(",");
    window.open(`${API}/invoice-batch?ids=${ids}&token=${token}`, "_blank");
  };

  const genBatchZip = async () => {
    if (selected.size < 1) { toast.error("Select at least one booking"); return; }
    // Save any pending amount edits for the selected rows first
    for (const id of selected) {
      const e = edits[id];
      if (e && e.amount && e.partner_id) {
        await api.post("/sales", { booking_id: id, currency: e.currency, amount: parseFloat(e.amount), partner_name: e.partner_name, partner_id: e.partner_id, gst_percent: e.gst_percent === -1 ? 0 : parseFloat(e.gst_percent||0), gst_mode: e.gst_percent === -1 ? "inclusive" : "exclusive" });
      }
    }
    const token = getToken();
    const ids = Array.from(selected).join(",");
    window.open(`${API}/invoice-batch/zip?ids=${ids}&token=${token}`, "_blank");
    toast.success(`Generating ${selected.size} invoice${selected.size === 1 ? "" : "s"}…`);
  };

  const submitStandalone = async () => {
    if (!standalone.customer.trim()) { toast.error("Customer required"); return; }
    if (!standalone.amount) { toast.error("Amount required"); return; }
    if (!standalone.partner_id) { toast.error("Pick a partner"); return; }
    try {
      const { data } = await api.post("/sales/standalone", {
        ...standalone,
        amount: parseFloat(standalone.amount) || 0,
        gst_percent: parseFloat(standalone.gst_percent) || 0,
        gst_mode: parseFloat(standalone.gst_percent) === -1 ? "inclusive" : "exclusive",
      });
      toast.success("Standalone invoice created");
      setStandaloneOpen(false);
      setStandalone({ customer:"", programme:"Package Tour Program", duration:"", start_date:"", end_date:"", email:"", telephone:"", currency:"USD", amount:"", partner_name:"", partner_id:"", gst_percent:0 });
      const token = getToken();
      window.open(`${API}/invoice/${data.id}?token=${token}`, "_blank");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pickStandalonePartner = (partnerId) => {
    const p = partners.find(x => x.id === partnerId);
    setStandalone(s => ({ ...s, partner_id: partnerId, partner_name: p ? (p.company_name || p.name || "") : "" }));
  };

  const deleteInvoice = async (r) => {
    try {
      await api.delete(`/sales/${r.id}`);
      toast.success("Invoice removed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Bulk delete — Founder-only, uses the same Recycle-Bin snapshot path
  // as the single-delete endpoint so a mistake is one-tap restorable.
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const { data } = await api.post("/sales/bulk-delete", { ids });
    setSelected(new Set());
    await load();
    if (data.deleted !== data.requested) {
      toast.info(`${data.deleted} of ${data.requested} rows removed (rest had no invoice yet)`);
    }
  };

  const runBulkConfirm = async () => {
    try {
      const { data } = await api.post("/sales/confirm-all-suggested");
      setBulkResult(data);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Row-level renderer — shared between the flat filtered view and the
  // partner-grouped view so both paths produce identical markup.
  const salesRow = (r) => {
    const e = edits[r.id] || {};
    const locked = isLocked(r);
    const gstLabel = (GST_OPTIONS.find(g => g.v === (e.gst_percent ?? 0)) || {}).label || "None";
    return (
      <tr key={r.id} data-testid={`sales-row-${r.id}`} className={locked ? "bg-[color:var(--aii-green-tint)]/30" : r.auto_priced ? "bg-amber-50/40" : ""} title={r.auto_priced ? "Auto-priced from the Pricing module — click Save to confirm." : undefined}>
        <td>
          <input type="checkbox" data-testid={`sales-sel-${r.id}`} checked={selected.has(r.id)} onChange={()=>toggleSel(r.id)} />
        </td>
        <td>
          <span className={`pill ${r.kind === "weekend" ? "pill-amber" : r.kind === "standalone" ? "pill-red" : "pill-green"}`}>
            {r.kind === "weekend" ? "Weekend" : r.kind === "standalone" ? "Standalone" : "Program"}
          </span>
        </td>
        <td className="font-medium">{r.customer}</td>
        <td className="text-sm text-brand font-medium" data-testid={`sales-partner-display-${r.id}`}>{r.partner_name || <span className="text-[color:var(--aii-muted)]">—</span>}</td>
        <td>{r.programme || "—"}</td>
        <td>{formatDuration(r.duration) || "—"}</td>
        <td>{fmtDate(r.start_date) || "—"}</td>
        <td>{fmtDate(r.end_date) || "—"}</td>
        <td><EmailLink value={r.email}/></td>
        <td><PhoneLink value={r.telephone}/></td>
        <td data-testid={`sales-currency-${r.id}`}>
          {locked ? (
            <span className="text-sm font-medium">{SYM[e.currency||"USD"]} {e.currency||"USD"}</span>
          ) : (
            <select value={e.currency||"USD"} onChange={ev=>setEdit(r.id,"currency",ev.target.value)}
              className="px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {CURR.map(c=><option key={c} value={c}>{SYM[c]} {c}</option>)}
            </select>
          )}
        </td>
        <td data-testid={`sales-amount-${r.id}`}>
          {locked ? (
            <span className="font-semibold text-brand">{SYM[e.currency||"USD"]}{Number(e.amount||0).toLocaleString()}</span>
          ) : (
            <input type="number" value={e.amount||""} onChange={ev=>setEdit(r.id,"amount",ev.target.value)}
              className="w-24 px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
          )}
        </td>
        <td data-testid={`sales-gst-${r.id}`}>
          {locked ? (
            <span className="text-sm">{gstLabel}</span>
          ) : (
            <select value={e.gst_percent ?? 0} onChange={ev=>setEdit(r.id,"gst_percent", parseFloat(ev.target.value))}
              className="px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              {GST_OPTIONS.map(g=><option key={g.v} value={g.v}>{g.label}</option>)}
            </select>
          )}
        </td>
        <td>
          {locked ? (
            <span className="text-sm font-medium" data-testid={`sales-partner-locked-${r.id}`}>{e.partner_name || "—"}</span>
          ) : (
            <select data-testid={`sales-partner-${r.id}`} value={e.partner_id || ""}
              onChange={ev => pickPartner(r.id, ev.target.value)}
              className="w-44 px-2 py-1.5 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
              <option value="">— pick partner —</option>
              {partners.map(p => <option key={p.id} value={p.id}>{p.company_name || p.name}</option>)}
            </select>
          )}
        </td>
        <td data-testid={`sales-status-${r.id}`}>
          {(() => {
            // Founder-only Paid / Partial / Due pill — sums non-rejected
            // partner_payments against this booking_id + currency. Tooltip
            // shows the paid vs invoice-total split so Founder doesn't
            // have to open the Payments drawer to see the delta.
            const st = r.payment_status;
            if (!st) return <span className="text-[color:var(--aii-muted)] text-xs">—</span>;
            const cls = st === "paid"
              ? "bg-emerald-100 text-emerald-800 border-emerald-300"
              : st === "partial"
                ? "bg-amber-100 text-amber-800 border-amber-300"
                : "bg-red-100 text-red-800 border-red-300";
            const label = st === "paid" ? "Paid" : st === "partial" ? "Partial" : "Due";
            const tot = (r.invoice_total || 0);
            const paid = (r.paid_amount || 0);
            const bal = (r.balance || 0);
            const sym = SYM[r.currency || "USD"] || "$";
            return (
              <span
                data-testid={`sales-status-pill-${r.id}`}
                title={`Paid ${sym}${paid.toLocaleString()} · Invoice ${sym}${tot.toLocaleString()} · Balance ${sym}${bal.toLocaleString()}`}
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border rounded whitespace-nowrap ${cls}`}>
                {label}
                {st !== "paid" && bal > 0 && (
                  <span className="normal-case tracking-normal font-mono text-[10px] font-normal opacity-90">· {sym}{bal.toLocaleString()}</span>
                )}
              </span>
            );
          })()}
        </td>
        <td>
          <div className="flex items-center gap-1">
            {locked && isFounder && (
              <button onClick={()=>unlock(r.id)} data-testid={`sales-edit-${r.id}`}
                title="Unlock this row for editing"
                className="flex items-center gap-1.5 text-xs border border-[color:var(--aii-border)] text-[color:var(--aii-text-2)] px-3 py-1.5 rounded-md hover:bg-white font-semibold whitespace-nowrap">
                <Pencil size={12}/> Edit
              </button>
            )}
            {!locked && r.kind !== "standalone" && (
              <button onClick={()=>save(r)} data-testid={`sales-save-${r.id}`}
                title="Save amount + partner without generating an invoice"
                className="flex items-center gap-1.5 text-xs border border-brand text-brand px-3 py-1.5 rounded-md hover:bg-[color:var(--aii-green-tint)] font-semibold whitespace-nowrap">
                <Save size={12}/> Save
              </button>
            )}
            <button onClick={()=>genInvoice(r)} data-testid={`sales-invoice-${r.id}`} className="flex items-center gap-1.5 text-xs bg-brand text-white px-3 py-1.5 rounded-md hover:bg-brand-dark font-semibold whitespace-nowrap"><FileText size={12}/> Invoice</button>
            {isFounder && <button onClick={()=>setConfirmingInvoice({row: r})} data-testid={`sales-delete-${r.id}`} title="Delete this invoice" className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5"><Trash2 size={13}/></button>}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">Sales Dashboard</h1>
        <SalesToolbar
          channel={channel} setChannel={setChannel}
          partnerFilter={partnerFilter} setPartnerFilter={setPartnerFilter} partners={partners}
          tab={tab} setTab={setTab}
          fy={fy} setFy={setFy} fyOptions={fyOptions}
          selectedSize={selected.size}
          onNewStandalone={() => setStandaloneOpen(true)}
          onGenBatch={genBatch}
          onGenBatchZip={genBatchZip}
        />
      </div>

      {/* salesRow: extracted as a local render helper so filter + partner-
          grouping paths share exactly the same row markup. */}
      {(() => { return null; })()}
      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {CURR.map(c => (
              <div key={c} data-testid={`fy-tot-${c}`} className="aii-card p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">FY {fy === "all" ? "All-time" : fy} · {c}</div>
                <div className="text-2xl font-display font-semibold text-brand mt-1">{SYM[c]}{(summary.total[c]||0).toLocaleString(undefined,{maximumFractionDigits: 0})}</div>
              </div>
            ))}
          </div>
          <details className="aii-card p-3 text-xs text-[color:var(--aii-text-2)]" data-testid="sales-source">
            <summary className="cursor-pointer font-semibold text-[color:var(--aii-text-1)] flex items-center gap-1.5">
              <Info size={12}/> Where does this total come from? <span className="text-[color:var(--aii-muted)] font-normal">(click to expand)</span>
            </summary>
            <div className="mt-2 space-y-1 pl-1">
              <div><span className="font-mono text-[10px] px-1 py-0.5 bg-slate-100 rounded">program</span> — {SYM.USD}{(summary.program?.USD||0).toLocaleString()} · {SYM.EUR}{(summary.program?.EUR||0).toLocaleString()} · {SYM.GBP}{(summary.program?.GBP||0).toLocaleString()} · {SYM.INR}{(summary.program?.INR||0).toLocaleString()} <span className="text-[color:var(--aii-muted)]">← sum of `sales.amount` where the program_booking still exists</span></div>
              <div><span className="font-mono text-[10px] px-1 py-0.5 bg-slate-100 rounded">weekend</span> — {SYM.USD}{(summary.weekend?.USD||0).toLocaleString()} · {SYM.EUR}{(summary.weekend?.EUR||0).toLocaleString()} · {SYM.GBP}{(summary.weekend?.GBP||0).toLocaleString()} · {SYM.INR}{(summary.weekend?.INR||0).toLocaleString()} <span className="text-[color:var(--aii-muted)]">← sum of `weekend_sales.amount` where the weekend_booking still exists</span></div>
              <div><span className="font-mono text-[10px] px-1 py-0.5 bg-slate-100 rounded">activity</span> — {SYM.USD}{(summary.activity?.USD||0).toLocaleString()} <span className="text-[color:var(--aii-muted)]">← Founder-approved activities × booked-participant count</span></div>
              <div><span className="font-mono text-[10px] px-1 py-0.5 bg-slate-100 rounded">trip</span> — {SYM.USD}{(summary.trip?.USD||0).toLocaleString()} <span className="text-[color:var(--aii-muted)]">← Founder-approved trips (total field)</span></div>
              <div><span className="font-mono text-[10px] px-1 py-0.5 bg-slate-100 rounded">standalone</span> — {SYM.USD}{(summary.standalone?.USD||0).toLocaleString()} · {SYM.EUR}{(summary.standalone?.EUR||0).toLocaleString()} · {SYM.GBP}{(summary.standalone?.GBP||0).toLocaleString()} · {SYM.INR}{(summary.standalone?.INR||0).toLocaleString()} <span className="text-[color:var(--aii-muted)]">← one-off invoices not tied to any booking</span></div>
              <div className="text-[color:var(--aii-muted)] text-[11px] pt-1 border-t border-[color:var(--aii-border)]/50 flex items-center justify-between gap-2">
                <span>Orphaned sales rows (booking deleted but sale left behind) are excluded — deleting a booking now cascades into its sale, arrival &amp; C-Form.</span>
                {isFounder && (
                  <div className="flex items-center gap-1.5">
                    <a
                      data-testid="sales-review-pdf"
                      href={`${API}/sales/suggested-review.pdf?token=${getToken()}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] font-semibold whitespace-nowrap"
                      title="Download a landscape PDF listing every auto-priced booking (partner, location, programme, price, GST, source) before you mass-confirm."
                    >
                      Review PDF
                    </a>
                    <button
                      data-testid="sales-confirm-all"
                      onClick={() => setConfirmingBulk(true)}
                      className="text-[10px] px-2 py-1 bg-brand text-white rounded hover:bg-brand-dark font-semibold whitespace-nowrap"
                    >
                      Confirm all suggested
                    </button>
                    <button
                      data-testid="sales-backfill-weekend"
                      onClick={async () => {
                        if (!window.confirm("Create weekend_sales rows for every approved weekend booking that has a tour_price but no sale? Fixes weekend revenue after upgrading. Safe & idempotent.")) return;
                        try {
                          const { data } = await api.post("/weekend-bookings/backfill-sales-legacy");
                          toast.success(`Backfilled ${data.backfilled_sales} weekend sales · ${data.already_sold} already sold`);
                          await load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}
                      className="text-[10px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 font-semibold whitespace-nowrap"
                    >
                      Backfill weekend sales
                    </button>
                    <button
                      data-testid="sales-backfill-preview"
                      onClick={async () => {
                        try {
                          const { data } = await api.post("/sales/backfill-missing?dry_run=true");
                          setBackfillPreview(data);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}
                      className="text-[10px] px-2 py-1 border border-sky-300 text-sky-700 rounded hover:bg-sky-50 font-semibold whitespace-nowrap"
                      title="Preview which bookings would be priced (and at what) without saving anything"
                    >
                      Preview backfill
                    </button>
                    <button
                      data-testid="sales-backfill-missing"
                      onClick={async () => {
                        if (!window.confirm("Repair every program + weekend booking that has NO sale row (application snapshot → Pricing rule → $0 stub). Safe & idempotent — the fastest way to recover missing revenue.")) return;
                        try {
                          const { data } = await api.post("/sales/backfill-missing");
                          const totalTxt = Object.entries(data.total_recovered_by_currency || {}).map(([c,v]) => `${SYM[c]||c}${Number(v||0).toLocaleString()}`).join(" · ") || "$0";
                          const repriced = (data.program_repriced || 0) + (data.weekend_repriced || 0);
                          const created = (data.program_created || 0) + (data.weekend_created || 0);
                          toast.success(`Recovered ${created} new sales · repriced ${repriced} · ${totalTxt}`);
                          await load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}
                      className="text-[10px] px-2 py-1 border border-sky-300 text-sky-700 rounded hover:bg-sky-50 font-semibold whitespace-nowrap"
                      title="One-click repair — creates missing sale rows for every unpriced program AND weekend booking"
                    >
                      Backfill missing sales
                    </button>
                    <button
                      data-testid="sales-cleanup-orphans"
                    onClick={async () => {
                      if (!window.confirm("Purge orphaned sales / arrivals / C-Forms whose booking no longer exists? Safe & reversible only via re-creating the booking.")) return;
                      try {
                        const { data } = await api.post("/sales/cleanup-orphans");
                        toast.success(`Cleaned ${data.orphan_program_sales_deleted} program + ${data.orphan_weekend_sales_deleted} weekend orphans`);
                        await load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                    }}
                    className="text-[10px] px-2 py-1 border border-red-300 text-red-700 rounded hover:bg-red-50 font-semibold whitespace-nowrap"
                  >
                    Purge orphans now
                  </button>
                  </div>
                )}
              </div>
            </div>
          </details>
        </>
      )}
      {isFounder && <WeekendRevenueChart fy={fy === "all" ? currentFY() : fy}/>}
      {isFounder && (
        <BulkActionBar
          count={selected.size}
          label="invoice row"
          onClear={() => setSelected(new Set())}
          onDelete={bulkDelete}
        />
      )}
      <div className="aii-card overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="aii-table">
            <thead><tr>
              <th className="w-8"></th><th>Kind</th><th>Participant</th><th>Partner</th><th>Programme</th><th>Duration</th><th>Start</th><th>End</th>
              <th>Email</th><th>Phone</th><th>Currency</th><th>Amount</th><th>GST</th><th>Partner</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={15} className="text-center py-10 text-[color:var(--aii-muted)]">No bookings.</td></tr>}
              {(() => {
                // Apply the channel filter first, then optionally cluster under
                // Partner headers. Program/Weekend/Standalone tabs filter by
                // r.kind so revenue reads cleanly by channel.
                const filtered = channel === "all" || channel === "partner"
                  ? rows
                  : rows.filter(r => (r.kind || "program") === channel);
                if (filtered.length === 0) return null;
                if (channel !== "partner") return filtered;
                const g = new Map();
                for (const r of filtered) {
                  const k = (r.partner_name || "").trim() || "— Unassigned partner —";
                  if (!g.has(k)) g.set(k, []);
                  g.get(k).push(r);
                }
                return { grouped: [...g.entries()].sort(([a],[b]) => a.localeCompare(b)) };
              })() && (() => {
                let filtered = channel === "all" || channel === "partner"
                  ? rows
                  : rows.filter(r => (r.kind || "program") === channel);
                if (partnerFilter) {
                  filtered = filtered.filter(r => (r.partner_name || "").toLowerCase() === partnerFilter);
                }
                if (filtered.length === 0) return <tr><td colSpan={14} className="text-center py-8 text-[color:var(--aii-muted)] italic">No sales match this filter.</td></tr>;
                if (channel !== "partner") return filtered.map(r => salesRow(r));
                const g = new Map();
                for (const r of filtered) {
                  const k = (r.partner_name || "").trim() || "— Unassigned partner —";
                  if (!g.has(k)) g.set(k, []);
                  g.get(k).push(r);
                }
                const totalsByCcy = (list) => {
                  const t = {};
                  for (const r of list) { const c = r.currency || "USD"; t[c] = (t[c] || 0) + (r.amount || 0); }
                  return Object.entries(t).map(([c, v]) => `${SYM[c]}${Math.round(v).toLocaleString()}`).join(" · ");
                };
                const out = [];
                for (const [pname, list] of [...g.entries()].sort(([a],[b]) => a.localeCompare(b))) {
                  const totalStr = totalsByCcy(list);
                  out.push(
                    <tr key={`ph-${pname}`} data-testid={`sales-partner-hdr-${pname}`} className="bg-[color:var(--aii-green-tint)]/50">
                      <td colSpan={14} className="px-3 py-1.5 text-[11px] uppercase tracking-widest font-bold text-brand">
                        {pname} · {list.length} sale{list.length === 1 ? "" : "s"}{totalStr ? ` · ${totalStr}` : ""}
                      </td>
                    </tr>
                  );
                  for (const r of list) out.push(salesRow(r));
                }
                return out;
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Standalone invoice modal */}
      {standaloneOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="standalone-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">One-off / last minute</div>
                <h3 className="text-lg font-display font-semibold">New standalone invoice</h3>
              </div>
              <button onClick={()=>setStandaloneOpen(false)}><X size={18}/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Customer name *</label>
                <input data-testid="std-customer" value={standalone.customer} onChange={e=>setStandalone(s=>({...s,customer:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Programme / description</label>
                <input data-testid="std-programme" value={standalone.programme} onChange={e=>setStandalone(s=>({...s,programme:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Duration</label>
                <input value={standalone.duration} onChange={e=>setStandalone(s=>({...s,duration:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm" placeholder="e.g. 3 days"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Partner (billed to) *</label>
                <select data-testid="std-partner" value={standalone.partner_id} onChange={e => pickStandalonePartner(e.target.value)} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  <option value="">— pick partner —</option>
                  {partners.map(p => <option key={p.id} value={p.id}>{p.company_name || p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Start date</label>
                <input type="date" value={standalone.start_date} onChange={e=>setStandalone(s=>({...s,start_date:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">End date</label>
                <input type="date" value={standalone.end_date} onChange={e=>setStandalone(s=>({...s,end_date:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Currency</label>
                <select data-testid="std-currency" value={standalone.currency} onChange={e=>setStandalone(s=>({...s,currency:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  {CURR.map(c=><option key={c} value={c}>{SYM[c]} {c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">Amount *</label>
                <input data-testid="std-amount" type="number" value={standalone.amount} onChange={e=>setStandalone(s=>({...s,amount:e.target.value}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--aii-text-2)]">GST</label>
                <select value={standalone.gst_percent} onChange={e=>setStandalone(s=>({...s,gst_percent:parseFloat(e.target.value)}))} className="mt-1 w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md bg-white text-sm">
                  {GST_OPTIONS.map(g=><option key={g.v} value={g.v}>{g.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={()=>setStandaloneOpen(false)} className="px-4 py-2 text-sm">Cancel</button>
              <button onClick={submitStandalone} data-testid="std-submit" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><FileText size={14}/> Create &amp; view invoice</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmingBulk}
        title="Confirm every auto-priced sale?"
        description={"This saves every amber (auto-priced) row at once — skipping any that already have a manually-saved sale.\nSafe & idempotent — you can run it again anytime."}
        confirmLabel="Confirm all"
        destructive={false}
        onConfirm={() => { setConfirmingBulk(false); runBulkConfirm(); }}
        onCancel={() => setConfirmingBulk(false)}
        testid="bulk-confirm"
      />

      <ConfirmDialog
        open={!!confirmingInvoice}
        title={confirmingInvoice?.row?.kind === "standalone" ? "Delete this standalone invoice?" : `Delete the invoice for ${confirmingInvoice?.row?.customer || "this participant"}?`}
        description="The invoice row will be removed permanently.\nThis action cannot be undone."
        confirmLabel="Delete invoice"
        onConfirm={() => { if (confirmingInvoice) deleteInvoice(confirmingInvoice.row); setConfirmingInvoice(null); }}
        onCancel={() => setConfirmingInvoice(null)}
        testid="invoice-delete-confirm"
      />

      <Dialog open={!!bulkResult} onOpenChange={(o) => { if (!o) setBulkResult(null); }}>
        <DialogContent data-testid="bulk-result-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="bulk-result-title">
              {bulkResult?.saved > 0
                ? `Saved ${bulkResult.saved} suggested price${bulkResult.saved === 1 ? "" : "s"} 🎉`
                : "Nothing new to confirm"}
            </DialogTitle>
            <DialogDescription>
              {bulkResult?.saved > 0
                ? "Every auto-priced row is now locked in. Review the breakdown below — anything looks off? Any single row can still be re-opened and edited."
                : "Every booking with a matching Pricing rule already has a saved sale. You're fully caught up."}
            </DialogDescription>
          </DialogHeader>
          {bulkResult?.saved > 0 && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold mb-1.5">Total by currency</div>
                <div className="flex flex-wrap gap-2" data-testid="bulk-totals">
                  {Object.entries(bulkResult.total_by_currency || {}).sort(([a],[b]) => a.localeCompare(b)).map(([ccy, amt]) => (
                    <span key={ccy} data-testid={`bulk-total-${ccy}`} className="px-3 py-1.5 rounded-md bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-semibold">
                      {SYM[ccy] || ccy}{Number(amt || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-[10px] opacity-70">{ccy}</span>
                    </span>
                  ))}
                </div>
              </div>
              {(bulkResult.top_partners || []).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)] font-semibold mb-1.5">Top {Math.min(5, bulkResult.top_partners.length)} partners</div>
                  <ol className="space-y-1.5" data-testid="bulk-top-partners">
                    {bulkResult.top_partners.map((p, i) => (
                      <li key={`tp-${p.name}`} data-testid={`bulk-top-${i}`} className="flex items-center justify-between gap-2 border-b border-[color:var(--aii-border)]/50 pb-1.5 last:border-0">
                        <span className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="w-5 h-5 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                          <span className="truncate font-semibold">{p.name}</span>
                        </span>
                        <span className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-[11px] text-[color:var(--aii-muted)]">{p.count} row{p.count === 1 ? "" : "s"}</span>
                          <span className="text-xs font-semibold text-brand">
                            {Object.entries(p.amount_by_currency || {}).map(([c, a]) =>
                              `${SYM[c] || c}${Number(a || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                            ).join(" · ")}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              data-testid="bulk-result-close"
              onClick={() => setBulkResult(null)}
              className="px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark"
            >
              Got it
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!backfillPreview} onOpenChange={(o) => { if (!o) setBackfillPreview(null); }}>
        <DialogContent data-testid="backfill-preview-modal" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="backfill-preview-title">
              Backfill preview · dry run
            </DialogTitle>
            <DialogDescription>
              Nothing has been saved yet. Review the rows below — click <b>Apply backfill</b> to persist,
              or close to cancel.
            </DialogDescription>
          </DialogHeader>
          {backfillPreview && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-2" data-testid="backfill-preview-summary">
                <span className="px-2 py-1 rounded-md bg-sky-100 text-sky-800 text-xs font-semibold">
                  {backfillPreview.program_created + backfillPreview.program_repriced} program rows
                </span>
                <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-semibold">
                  {backfillPreview.weekend_created + backfillPreview.weekend_repriced} weekend rows
                </span>
                {Object.entries(backfillPreview.total_recovered_by_currency || {}).map(([c, v]) => (
                  <span key={`bp-tot-${c}`} className="px-2 py-1 rounded-md bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] text-xs font-semibold">
                    +{SYM[c] || c}{Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} {c}
                  </span>
                ))}
              </div>
              {(backfillPreview.previews || []).length === 0 ? (
                <div className="text-[color:var(--aii-muted)] italic py-6 text-center" data-testid="backfill-preview-empty">
                  Nothing to backfill — every booking already has a priced sale row. You&apos;re fully caught up.
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto border border-[color:var(--aii-border)] rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-[color:var(--aii-bg)] sticky top-0">
                      <tr className="text-left">
                        <th className="px-2 py-2 font-semibold">Action</th>
                        <th className="px-2 py-2 font-semibold">Kind</th>
                        <th className="px-2 py-2 font-semibold">Participant</th>
                        <th className="px-2 py-2 font-semibold">Partner</th>
                        <th className="px-2 py-2 font-semibold">Programme</th>
                        <th className="px-2 py-2 font-semibold text-right">Price</th>
                        <th className="px-2 py-2 font-semibold">Source</th>
                      </tr>
                    </thead>
                    <tbody data-testid="backfill-preview-rows">
                      {(backfillPreview.previews || []).map((p, i) => (
                        <tr key={`bp-${p.booking_id}-${i}`} data-testid={`bp-row-${i}`}
                            className="border-t border-[color:var(--aii-border)]/50">
                          <td className="px-2 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${
                              p.action === "create" ? "bg-emerald-100 text-emerald-800"
                              : p.action === "reprice" ? "bg-amber-100 text-amber-800"
                              : "bg-slate-100 text-slate-600"
                            }`}>{p.action}</span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase font-bold ${
                              p.kind === "weekend" ? "bg-emerald-50 text-emerald-800" : "bg-sky-50 text-sky-800"
                            }`}>{p.kind}</span>
                          </td>
                          <td className="px-2 py-1.5 truncate max-w-[8rem]">{p.customer}</td>
                          <td className="px-2 py-1.5 truncate max-w-[8rem]">{p.partner_name}</td>
                          <td className="px-2 py-1.5 truncate max-w-[10rem]">{p.programme}</td>
                          <td className="px-2 py-1.5 text-right font-semibold whitespace-nowrap">
                            {p.price > 0
                              ? `${SYM[p.currency] || p.currency}${Number(p.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                              : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-[color:var(--aii-muted)] truncate max-w-[10rem]">{p.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button
              data-testid="backfill-preview-cancel"
              onClick={() => setBackfillPreview(null)}
              className="px-4 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              data-testid="backfill-preview-apply"
              disabled={(backfillPreview?.previews || []).length === 0}
              onClick={async () => {
                try {
                  const { data } = await api.post("/sales/backfill-missing");
                  const totalTxt = Object.entries(data.total_recovered_by_currency || {}).map(([c,v]) => `${SYM[c]||c}${Number(v||0).toLocaleString()}`).join(" · ") || "$0";
                  const created = (data.program_created || 0) + (data.weekend_created || 0);
                  const repriced = (data.program_repriced || 0) + (data.weekend_repriced || 0);
                  toast.success(`Applied · ${created} new · repriced ${repriced} · ${totalTxt}`);
                  setBackfillPreview(null);
                  await load();
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
              }}
              className="px-4 py-2 bg-brand text-white font-semibold rounded-md hover:bg-brand-dark disabled:opacity-50"
            >
              Apply backfill
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
